import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Tooltip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardHeader,
  Grid,
  LinearProgress
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  Security as SecurityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import { useAdminUsers } from '../../../../hooks/useAdminUsers';
import { toast } from 'react-toastify';

// Available roles based on backend
const AVAILABLE_ROLES = [
  { value: 'USER', label: 'User', color: 'default' },
  { value: 'ADMIN', label: 'Admin', color: 'error' },
  { value: 'SITE_OWNER', label: 'Site Owner', color: 'warning' }
];

const AdminUserManagement = () => {
  // ────────────── REDUX STATE ──────────────
  
  const {
    // Users data
    users,
    usersLoading,
    usersError,
    usersPage,
    usersRowsPerPage,
    usersTotalElements,
    usersTotalPages,
    
    // Selected user details
    selectedUser,
    selectedUserLoading,
    selectedUserError,
    
    // User actions
    actionLoading,
    actionError,
    
    // Export
    exportLoading,
    exportError,
    
    // Actions
    loadUsers,
    loadUserById,
    handleEnableUser,
    handleDisableUser,
    handleAddUserRole,
    handleRemoveUserRole,
    handleDeleteUser,
    handleExportUsersPurchasesToExcel,
    handleUsersPageChange,
    handleUsersRowsPerPageChange,
    clearAllErrors,
    clearUserDetails
  } = useAdminUsers();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // ────────────── LOCAL STATE ──────────────
  
  // Search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // User management dialogs
  const [userDetailsDialog, setUserDetailsDialog] = useState(false);
  const [roleDialog, setRoleDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [newRole, setNewRole] = useState('');
  
  // Tab management
  const [activeTab, setActiveTab] = useState(0);

  // ────────────── DATA FETCHING ──────────────

  const fetchUsers = useCallback(() => {
    const params = {
      page: usersPage,
      size: usersRowsPerPage,
      sort: 'id,desc' // Default sorting
    };
    
    // Add search parameter if provided
    if (searchTerm.trim()) {
      params.search = searchTerm.trim();
    }
    
    loadUsers(params);
  }, [loadUsers, usersPage, usersRowsPerPage, searchTerm]);

  // ────────────── EFFECTS ──────────────

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ────────────── EVENT HANDLERS ──────────────

  const handlePageChange = (event, newPage) => {
    handleUsersPageChange(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    handleUsersRowsPerPageChange(parseInt(event.target.value, 10));
  };

  const handleSearch = () => {
    handleUsersPageChange(0);
    fetchUsers();
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setRoleFilter('');
    setStatusFilter('');
    handleUsersPageChange(0);
  };

  const handleUserDetails = async (userId) => {
    try {
      loadUserById(userId);
      setUserDetailsDialog(true);
    } catch (err) {
      toast.error(`Failed to fetch user details: ${err.message}`);
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      if (currentStatus === 'ENABLED') {
        handleDisableUser(userId);
        toast.success('User disabled successfully');
      } else {
        handleEnableUser(userId);
        toast.success('User enabled successfully');
      }
    } catch (err) {
      toast.error(`Failed to update user status: ${err.message}`);
    }
  };

  const handleAddRole = async () => {
    if (!selectedUser || !newRole) return;
    
    try {
      handleAddUserRole(selectedUser.id, newRole);
      toast.success(`Role ${newRole} added successfully`);
      
      // Refresh user details
      loadUserById(selectedUser.id);
      
      setRoleDialog(false);
      setNewRole('');
    } catch (err) {
      toast.error(`Failed to add role: ${err.message}`);
    }
  };

  const handleRemoveRole = async (userId, roleName) => {
    try {
      handleRemoveUserRole(userId, roleName);
      toast.success(`Role ${roleName} removed successfully`);
      
      // Refresh user details
      loadUserById(userId);
    } catch (err) {
      toast.error(`Failed to remove role: ${err.message}`);
    }
  };

  const handleDeleteUserAction = async () => {
    if (!selectedUser) return;
    
    try {
      handleDeleteUser(selectedUser.id);
      toast.success('User deleted successfully');
      
      setDeleteDialog(false);
      setUserDetailsDialog(false);
      clearUserDetails();
    } catch (err) {
      toast.error(`Failed to delete user: ${err.message}`);
    }
  };

  const handleExportPurchases = async () => {
    try {
      // Get current year and months for export
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;
      
      handleExportUsersPurchasesToExcel([currentMonth], currentYear);
      
      toast.success('Purchase data exported successfully');
    } catch (err) {
      toast.error(`Failed to export purchases: ${err.message}`);
    }
  };

  // ────────────── RENDER HELPERS ──────────────

  const getRoleChip = (role) => {
    const roleConfig = AVAILABLE_ROLES.find(r => r.value === role) || { 
      value: role, 
      label: role, 
      color: 'default' 
    };
    return (
      <Chip
        key={role}
        label={roleConfig.label}
        color={roleConfig.color}
        size="small"
        variant="outlined"
      />
    );
  };

  const getStatusChip = (status) => {
    return (
      <Chip
        label={status}
        color={status === 'ENABLED' ? 'success' : 'error'}
        size="small"
        variant="filled"
      />
    );
  };

  const renderUsersTable = () => (
    <TableContainer
  component={Paper}
  elevation={2}
  sx={{
    width: '100%',
    overflowX: 'auto',
  }}
>
      <Table>
      <TableHead>
  <TableRow sx={{ backgroundColor: 'primary.main' }}>
    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>ID</TableCell>
    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Username</TableCell>
    <TableCell 
      sx={{ color: 'white', fontWeight: 'bold', display: { xs: 'none', sm: 'table-cell' } }}
    >
      Email
    </TableCell>
    <TableCell 
      sx={{ color: 'white', fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}
    >
      Roles
    </TableCell>
    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
  </TableRow>
</TableHead>

        <TableBody>
          {usersLoading ? (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Box display="flex" alignItems="center" justifyContent="center" py={4}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    Loading users...
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : users.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography variant="body2" color="text.secondary" py={4}>
                  No users found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            users.map((user) => (
              <TableRow key={user.id} hover>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{user.id}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{user.username}</TableCell>
        <TableCell
          sx={{ display: { xs: 'none', sm: 'table-cell' }, whiteSpace: 'nowrap' }}
        >
          {user.email}
        </TableCell>
        <TableCell
          sx={{ display: { xs: 'none', md: 'table-cell' } }}
        >
          <Box display="flex" gap={0.5} flexWrap="wrap">
            {user.roles?.map(role => getRoleChip(role))}
          </Box>
        </TableCell>
                <TableCell>{getStatusChip(user.status || 'ENABLED')}</TableCell>
                <TableCell>
                  <Box display="flex" gap={1}>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleUserDetails(user.id)}
                        disabled={actionLoading}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title={user.status === 'ENABLED' ? 'Disable User' : 'Enable User'}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleUserStatus(user.id, user.status)}
                        disabled={actionLoading}
                        color={user.status === 'ENABLED' ? 'error' : 'success'}
                      >
                        {user.status === 'ENABLED' ? <PersonRemoveIcon /> : <PersonAddIcon />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      
      <TablePagination
  rowsPerPageOptions={[10, 25, 50, 100, 250]}
  component="div"
  count={usersTotalElements}
  rowsPerPage={usersRowsPerPage}
  page={usersPage}
  onPageChange={handlePageChange}
  onRowsPerPageChange={handleRowsPerPageChange}
  labelRowsPerPage={isMobile ? 'Rows' : 'Rows per page'}
  sx={{
    '.MuiTablePagination-toolbar': {
      flexWrap: 'wrap',
      rowGap: 1,
    },
  }}
/>

    </TableContainer>
  );

  const renderUserDetailsDialog = () => (
<Dialog
  open={userDetailsDialog}
  onClose={() => setUserDetailsDialog(false)}
  maxWidth="md"
  fullWidth
  fullScreen={isMobile}
>

      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PersonAddIcon />
          User Details: {selectedUser?.username}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {selectedUser && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardHeader title="Basic Information" />
                <CardContent>
                  <Typography variant="body2" gutterBottom>
                    <strong>ID:</strong> {selectedUser.id}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Username:</strong> {selectedUser.username}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Email:</strong> {selectedUser.email}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Status:</strong> {getStatusChip(selectedUser.status || 'ENABLED')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardHeader 
                  title="Roles" 
                  action={
                    <Button
                      size="small"
                      startIcon={<SecurityIcon />}
                      onClick={() => setRoleDialog(true)}
                    >
                      Add Role
                    </Button>
                  }
                />
                <CardContent>
                  <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                    {selectedUser.roles?.map(role => (
                      <Box key={role} display="flex" alignItems="center" gap={0.5}>
                        {getRoleChip(role)}
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveRole(selectedUser.id, role)}
                          disabled={actionLoading}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => setDeleteDialog(true)}
          disabled={actionLoading}
        >
          Delete User
        </Button>
        <Button onClick={() => setUserDetailsDialog(false)}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderRoleDialog = () => (
<Dialog
  open={roleDialog}
  onClose={() => setRoleDialog(false)}
  maxWidth="sm"
  fullWidth
  fullScreen={isMobile}
>
      <DialogTitle>Add Role to User</DialogTitle>
      
      <DialogContent>
        <FormControl fullWidth margin="normal">
          <InputLabel>Select Role</InputLabel>
          <Select
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
            label="Select Role"
          >
            {AVAILABLE_ROLES.map(role => (
              <MenuItem key={role.value} value={role.value}>
                {role.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={() => setRoleDialog(false)}>
          Cancel
        </Button>
        <Button
          onClick={handleAddRole}
          variant="contained"
          disabled={!newRole || actionLoading}
        >
          Add Role
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderDeleteDialog = () => (
<Dialog
  open={deleteDialog}
  onClose={() => setDeleteDialog(false)}
  maxWidth="sm"
  fullWidth
  fullScreen={isMobile}
>
      <DialogTitle color="error">Delete User</DialogTitle>
      
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Are you sure you want to delete user "{selectedUser?.username}"? 
          This action cannot be undone.
        </Alert>
        <Typography variant="body2" color="text.secondary">
          This will permanently remove the user and all associated data.
        </Typography>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={() => setDeleteDialog(false)}>
          Cancel
        </Button>
        <Button
          onClick={handleDeleteUserAction}
          color="error"
          variant="contained"
          disabled={actionLoading}
        >
          Delete User
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ────────────── MAIN RENDER ──────────────

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
  display="flex"
  justifyContent="space-between"
  alignItems={{ xs: 'flex-start', sm: 'center' }}
  flexDirection={{ xs: 'column', sm: 'row' }}
  gap={2}
  mb={3}
>
  <Typography
    variant={isMobile ? 'h5' : 'h4'}
    component="h1"
    fontWeight="bold"
  >
    Admin User Management
  </Typography>
  
  <Box
    display="flex"
    gap={1.5}
    flexWrap="wrap"
    justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
  >
    <Button
      variant="outlined"
      startIcon={<DownloadIcon />}
      onClick={handleExportPurchases}
      disabled={actionLoading}
      size={isMobile ? 'small' : 'medium'}
    >
      Export Purchases
    </Button>
    
    <Button
      variant="contained"
      startIcon={<RefreshIcon />}
      onClick={fetchUsers}
      disabled={usersLoading}
      size={isMobile ? 'small' : 'medium'}
    >
      Refresh
    </Button>
  </Box>
</Box>


      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
  <Box
    display="flex"
    gap={2}
    alignItems={{ xs: 'stretch', sm: 'center' }}
    flexDirection={{ xs: 'column', sm: 'row' }}
    flexWrap="wrap"
  >
    <TextField
      label="Search users"
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      size="small"
      fullWidth={isMobile}
      sx={{ minWidth: isMobile ? '100%' : 200 }}
      InputProps={{
        startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
      }}
    />
    
    <FormControl
      size="small"
      sx={{ minWidth: isMobile ? '100%' : 150 }}
      fullWidth={isMobile}
    >
      <InputLabel>Filter by Role</InputLabel>
      <Select
        value={roleFilter}
        onChange={(e) => setRoleFilter(e.target.value)}
        label="Filter by Role"
      >
        <MenuItem value="">All Roles</MenuItem>
        {AVAILABLE_ROLES.map(role => (
          <MenuItem key={role.value} value={role.value}>
            {role.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
    
    <Box
      display="flex"
      gap={1}
      justifyContent={{ xs: 'flex-start', sm: 'flex-start' }}
      flexWrap="wrap"
    >
      <Button
        variant="contained"
        onClick={handleSearch}
        disabled={usersLoading}
        size={isMobile ? 'small' : 'medium'}
      >
        Search
      </Button>
      
      <Button
        variant="outlined"
        startIcon={<ClearIcon />}
        onClick={handleClearSearch}
        size={isMobile ? 'small' : 'medium'}
      >
        Clear
      </Button>
    </Box>
  </Box>
</Paper>


      {/* Error Display */}
      {(usersError || actionError) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {usersError || actionError}
        </Alert>
      )}

      {/* Loading Indicator */}
      {usersLoading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Users Table */}
      {renderUsersTable()}

      {/* Dialogs */}
      {renderUserDetailsDialog()}
      {renderRoleDialog()}
      {renderDeleteDialog()}
    </Box>
  );
};

export default AdminUserManagement;
